/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Tab, Tabs } from "@nordcloud/gnui";
import { Events } from "~/components/Events";
import { useTabsWithUrl, useEstateFetch } from "~/hooks";
import {
  disabledFilterFields,
  DisabledFilters,
} from "~/views/estate/components/EstateFilters";
import { EstateRecords } from "~/views/estate/EstateRecords";
import { RouteParams } from "../types";

type Props = {
  setEstateRecordsLoading?: (loading: boolean) => void;
};

export function EnvironmentDetailsTabs({ setEstateRecordsLoading }: Props) {
  const { environment: envNid } = useParams<RouteParams>();
  const { activeTab, setActiveTab } = useTabsWithUrl();

  const { result, ...filters } = useEstateFetch({
    defaultFilters: {
      environment: [envNid],
    },
    skipFields: disabledFilterFields.get(DisabledFilters.APPS),
    activeTab,
  });
  const { data, loading } = result;

  useEffect(() => {
    if (setEstateRecordsLoading != null) {
      setEstateRecordsLoading(loading);
    }
  }, [loading, setEstateRecordsLoading]);

  const estateRecordsData = useMemo(() => {
    return {
      ...result,
      data: {
        estateV2: data,
      },
    };
  }, [data, result]);

  return (
    <Tabs step={activeTab} handleTab={setActiveTab}>
      <Tab heading="Estate records" caption="List of estate records">
        <EstateRecords
          allowSavingFilters
          envNid={envNid}
          queryFilters={filters}
          excludeColumns={["selection", "application", "actions"]}
          disabledFilters={[DisabledFilters.APPS]}
          dataEstate={estateRecordsData}
        />
      </Tab>
      <Tab heading="Events" caption="List of Events">
        <Events nid={envNid} />
      </Tab>
    </Tabs>
  );
}
